<template>
  <BasePanel headline="lang_AZ_ARTIST_PORTAL_CAPTION_TITLE" :alvqa="'artist-portal-' + azId">
    <template v-slot:panelContent>
    <div class="d-flex justify-end mb-2"><v-btn text color="buttonStandard" :loading="loading" @click="load">{{ $t('lang_AZ_ARTIST_PORTAL_CAPTION_REFRESH') }}</v-btn></div>
      <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
      <p>{{ $t('lang_AZ_ARTIST_PORTAL_INFO_INVITE') }}</p>
      <v-form @submit.prevent="invite">
        <v-row align="center">
          <v-col cols="12" md="7">
            <div class="d-flex align-center">
              <label :for="'artist-portal-email-' + azId" class="artist-portal-email-label mr-3 mb-0">{{ $t('lang_GENERIC_AZ_CAPTION_EMAIL') }}</label>
              <v-text-field :id="'artist-portal-email-' + azId" class="artist-portal-email" v-model="email" type="email" outlined dense hide-details />
            </div>
          </v-col>
          <v-col><v-btn color="buttonStandard" depressed class="white--text" type="submit" :loading="busy" :disabled="!email || loading">{{ $t('lang_AZ_ARTIST_PORTAL_BUTTON_CREATE_INVITATION') }}</v-btn></v-col>
        </v-row>
      </v-form>
      <div v-if="invitationUrl" class="mt-4">
        <v-text-field :value="invitationUrl" :label="$t('lang_AZ_ARTIST_PORTAL_CAPTION_INVITATION_LINK')" readonly outlined hide-details @focus="$event.target.select()" />
        <v-btn class="mt-2" outlined @click="copy">{{ copied ? $t('lang_AZ_ARTIST_PORTAL_BUTTON_COPIED') : $t('lang_AZ_ARTIST_PORTAL_BUTTON_COPY_LINK') }}</v-btn>
        <p class="text-caption mt-2">{{ $t('lang_AZ_ARTIST_PORTAL_INFO_INVITATION_LINK') }}</p>
      </div>
      <template v-if="loaded">
        <h3 class="mt-5 mb-2">{{ $t('lang_AZ_ARTIST_PORTAL_CAPTION_INVITATIONS') }}</h3>
        <v-simple-table><thead><tr><th>{{ $t('lang_GENERIC_AZ_CAPTION_EMAIL') }}</th><th>{{ $t('lang_AZ_ARTIST_PORTAL_CAPTION_STATUS') }}</th><th>{{ $t('lang_AZ_ARTIST_PORTAL_CAPTION_EXPIRES') }}</th><th/></tr></thead><tbody><tr v-for="row in invitations" :key="row.id"><td>{{ row.email }}</td><td>{{ statusLabel(row.status) }}</td><td>{{ formatExpiry(row.expiresAt) }}</td><td><v-btn v-if="row.status === 'pending'" small text :disabled="busy" @click="revoke('revokeInvitation', row.id)">{{ $t('lang_AZ_ARTIST_PORTAL_BUTTON_REVOKE') }}</v-btn></td></tr><tr v-if="!invitations.length"><td colspan="4">{{ $t('lang_AZ_ARTIST_PORTAL_EMPTY_INVITATIONS') }}</td></tr></tbody></v-simple-table>
        <h3 class="mt-5 mb-2">{{ $t('lang_AZ_ARTIST_PORTAL_CAPTION_ACCESS') }}</h3>
        <v-simple-table><thead><tr><th>{{ $t('lang_CAPTION_NAME') }}</th><th>{{ $t('lang_GENERIC_AZ_CAPTION_EMAIL') }}</th><th>{{ $t('lang_AZ_ARTIST_PORTAL_CAPTION_STATUS') }}</th><th/></tr></thead><tbody><tr v-for="row in memberships" :key="row.id"><td>{{ row.name }}</td><td>{{ row.email }}</td><td>{{ statusLabel(row.status) }}</td><td><v-btn v-if="row.status === 'active'" small text :disabled="busy" @click="revoke('revokeMembership', row.id)">{{ $t('lang_AZ_ARTIST_PORTAL_BUTTON_REVOKE_ACCESS') }}</v-btn></td></tr><tr v-if="!memberships.length"><td colspan="4">{{ $t('lang_AZ_ARTIST_PORTAL_EMPTY_ACCOUNTS') }}</td></tr></tbody></v-simple-table>
        <h3 class="mt-5 mb-2">{{ $t('lang_AZ_ARTIST_PORTAL_CAPTION_STATEMENTS') }}</h3>
        <p>{{ $t('lang_AZ_ARTIST_PORTAL_INFO_VISIBILITY') }}</p>
        <v-select
          v-model="publisher"
          :items="[{ text: $t('lang_AZ_ARTIST_PORTAL_FILTER_ALL_PUBLISHERS'), value: null }, ...publisherOptions]"
          :label="$t('lang_GENERIC_CAPTION_PUBLISHING_COMPANY')"
          outlined dense clearable hide-details
          class="artist-portal-publisher mb-4"
        />
        <v-simple-table>
          <thead><tr><th>{{ $t('lang_GENERIC_CAPTION_PERIOD') }}</th><th>{{ $t('lang_ERP_INVOICES_DROPDOWN_TYPE_CREDIT_NOTE') }}</th><th>{{ $t('lang_PUBLISHING_STATEMENTS_CAPTION_AMOUNT') }} {{ homeCurrency }}</th><th>{{ $t('lang_AZ_ARTIST_PORTAL_CAPTION_VISIBILITY') }}</th><th>{{ $t('lang_GENERAL_CAPTION_ICON_DOWNLOAD') }}</th></tr></thead>
          <tbody>
            <tr v-for="row in filteredStatements" :key="row.id">
              <td>{{ row.period }}</td>
              <td>{{ row.reference }}</td>
              <td>{{ formatAmount(row.amount) }}</td>
              <td><v-btn small :outlined="!Number(row.published)" :color="Number(row.published) ? 'primary' : undefined" :disabled="busy" @click="publish(row)">{{ Number(row.published) ? $t('lang_AZ_ARTIST_PORTAL_BUTTON_WITHDRAW') : $t('lang_AZ_ARTIST_PORTAL_BUTTON_PUBLISH') }}</v-btn></td>
              <td><v-btn small text color="buttonStandard" :loading="downloading === row.id" :disabled="!row.downloadUrl || downloading !== null" :title="row.downloadUrl ? $t('lang_AZ_ARTIST_PORTAL_TOOLTIP_DOWNLOAD') : $t('lang_AZ_ARTIST_PORTAL_FILE_UNAVAILABLE')" @click="download(row)"><v-icon small left>cloud_download</v-icon>{{ $t('lang_GENERAL_CAPTION_ICON_DOWNLOAD') }}</v-btn></td>
            </tr>
            <tr v-if="!filteredStatements.length"><td colspan="5">{{ statements.length ? $t('lang_AZ_ARTIST_PORTAL_EMPTY_FILTERED_STATEMENTS') : $t('lang_AZ_ARTIST_PORTAL_EMPTY_STATEMENTS') }}</td></tr>
          </tbody>
        </v-simple-table>
      </template>
    </template>
  </BasePanel>
</template>
<script>
import axios from 'axios'
import api from '@/api_alv'
import BasePanel from '@/components/_base/BasePanel'

export default {
  name: 'ArtistPortalAccess',
  components: { BasePanel },
  props: { azId: { type: String, required: true }, initialEmail: { type: String, default: '' } },
  data() { return { email: this.initialEmail || '', invitationUrl: '', copied: false, invitations: [], memberships: [], statements: [], publisher: null, loading: false, loaded: false, busy: false, downloading: null, error: '', loadVersion: 0 } },
  computed: {
    account() { return this.$store.getters.getUser || {} },
    homeCurrency() { return this.account.currency || (this.statements[0] && this.statements[0].currency) || '' },
    endpoint() { return api.rootUrl + 'artist-portal/artists/' + encodeURIComponent(this.azId) },
    publisherOptions() {
      const companies = new Map();
      this.statements.forEach(row => {
        const id = Number(row.publisherId);
        companies.set(id, row.publisherName || (id ? this.$t('lang_GENERIC_CAPTION_PUBLISHING_COMPANY') + ' #' + id : this.$t('lang_AZ_ARTIST_PORTAL_PUBLISHER_UNAVAILABLE')));
      });
      return Array.from(companies, ([value, text]) => ({ value, text })).sort((a, b) => a.text.localeCompare(b.text));
    },
    filteredStatements() {
      return this.publisher === null ? this.statements : this.statements.filter(row => Number(row.publisherId) === this.publisher);
    }
  },
  watch: { azId: { immediate: true, handler() { this.publisher = null; this.invitationUrl = ''; this.email = this.initialEmail || ''; this.load() } } },
  methods: {
    formatAmount(value) {
      if (value === null || value === undefined || value === '') return '';
      const amount = Number(value);
      if (!Number.isFinite(amount)) return '';
      const format = this.$alvPlugin.isZeroDecimalCurrency(this.homeCurrency) ? 'numberInteger' : 'number';
      return this.$n(amount, format, this.$i18n.locale);
    },
    formatExpiry(value) {
      if (!value) return '';
      // Older staff API responses contain a UTC SQL datetime without an offset.
      const timestamp = String(value).replace(' ', 'T');
      const date = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(timestamp) ? timestamp : timestamp + 'Z');
      if (Number.isNaN(date.getTime())) return '';
      return this.$d(date, {
        key: 'alvDateTime',
        locale: this.$i18n.locale,
        timeZone: 'Europe/Berlin'
      });
    },
    statusLabel(status) {
      const keys = {
        pending: 'lang_AZ_ARTIST_PORTAL_STATUS_PENDING',
        accepted: 'lang_AZ_ARTIST_PORTAL_STATUS_ACCEPTED',
        expired: 'lang_AZ_ARTIST_PORTAL_STATUS_EXPIRED',
        revoked: 'lang_AZ_ARTIST_PORTAL_STATUS_REVOKED',
        active: 'lang_AZ_ARTIST_PORTAL_STATUS_ACTIVE'
      };
      return this.$t(keys[status] || 'lang_AZ_ARTIST_PORTAL_STATUS_UNKNOWN');
    },
    errorMessage(e) {
      if (!e.response) return this.$t('lang_AZ_ARTIST_PORTAL_ERROR_CONNECTION');
      // The staff API currently returns English messages; localize its known errors.
      const keys = {
        'Email is required.': 'lang_AZ_ARTIST_PORTAL_ERROR_EMAIL',
        'Enter a valid email address.': 'lang_AZ_ARTIST_PORTAL_ERROR_EMAIL',
        'Company administrator access is required.': 'lang_AZ_ARTIST_PORTAL_ERROR_ADMIN',
        'Invalid A-Z ID.': 'lang_AZ_ARTIST_PORTAL_ERROR_ARTIST',
        'Artist not found.': 'lang_AZ_ARTIST_PORTAL_ERROR_ARTIST',
        'Access not found.': 'lang_AZ_ARTIST_PORTAL_ERROR_ACCESS',
        'Statement not found.': 'lang_AZ_ARTIST_PORTAL_ERROR_STATEMENT',
        'Generate the statement file before publishing.': 'lang_AZ_ARTIST_PORTAL_ERROR_GENERATE_FILE',
        'Configure ALV_PORTAL_ORIGIN first.': 'lang_AZ_ARTIST_PORTAL_ERROR_CONFIGURATION',
        'Portal is not configured or temporarily unavailable.': 'lang_AZ_ARTIST_PORTAL_ERROR_CONFIGURATION'
      };
      const message = e.response.data && e.response.data.error;
      return this.$t(keys[message] || 'lang_AZ_ARTIST_PORTAL_ERROR_REQUEST');
    },
    async load() {
      const version = ++this.loadVersion; this.loading = true; this.error = ''; this.loaded = false;
      try { const { data } = await axios.get(this.endpoint, api.getConfig()); if (version !== this.loadVersion) return; this.invitations = data.invitations; this.memberships = data.memberships; this.statements = data.statements; this.loaded = true }
      catch(e) { if (version === this.loadVersion) this.error = this.errorMessage(e) }
      finally { if (version === this.loadVersion) this.loading = false }
    },
    async mutate(payload) { const { data } = await axios.post(this.endpoint, payload, api.getConfig()); return data },
    async invite() { this.busy = true; this.error = ''; this.copied = false; this.invitationUrl = ''; try { const result = await this.mutate({ action: 'invite', email: this.email }); this.invitationUrl = result.url; await this.load() } catch(e) { this.error = this.errorMessage(e) } finally { this.busy = false } },
    async revoke(action, id) { this.busy = true; this.error = ''; try { await this.mutate({ action, id: Number(id) }); this.invitationUrl = ''; await this.load() } catch(e) { this.error = this.errorMessage(e) } finally { this.busy = false } },
    async publish(row) { this.busy = true; this.error = ''; try { await this.mutate({ action: 'publish', id: Number(row.id), visible: !Number(row.published) }); await this.load() } catch(e) { this.error = this.errorMessage(e) } finally { this.busy = false } },
    async download(row) {
      if (!row.downloadUrl || this.downloading !== null) return;
      this.downloading = row.id; this.error = '';
      try { await api.getFileFromALV(row.downloadUrl) }
      catch(e) { this.error = this.$t('lang_AZ_ARTIST_PORTAL_ERROR_DOWNLOAD') }
      finally { this.downloading = null }
    },
    async copy() { try { await navigator.clipboard.writeText(this.invitationUrl); this.copied = true } catch(e) { this.error = this.$t('lang_AZ_ARTIST_PORTAL_ERROR_COPY') } }
  }
}
</script>

<style scoped>
/* Restore centered input content after the shared checkbox slot override. */
.artist-portal-email ::v-deep .v-input__slot,
.artist-portal-publisher ::v-deep .v-input__slot {
  align-items: center !important;
}
.artist-portal-publisher {
  max-width: 360px;
}
.artist-portal-email-label {
  flex-shrink: 0;
  white-space: nowrap;
}
.artist-portal-email {
  min-width: 0;
}
</style>
